import { useState, useMemo } from 'react';
import { mockLeads } from '@/mocks/leads';
import { mockPayoutLines } from '@/mocks/payouts';
import { mockVerticals } from '@/mocks/verticals';
import { mockGeos } from '@/mocks/geos';
import { mockPartners } from '@/mocks/partners';
import BaseSelect from '@/components/base/BaseSelect';
import { LayoutGrid, BarChart3, Download, Save, Share2 } from 'lucide-react';

const dimensions = ['Vertical', 'Geo', 'Partner', 'Status', 'Routing Status', 'Month'];
const metrics = ['Leads received', 'Leads routed', 'Gross payout EUR', 'Avg quality score', 'Disputes raised'];

export default function ReportsPage() {
  const [groupBy, setGroupBy] = useState('Vertical');
  const [selectedMetrics, setSelectedMetrics] = useState(['Leads received', 'Gross payout EUR']);
  const [viewMode, setViewMode] = useState<'list' | 'chart'>('list');

  const data = useMemo(() => {
    const leads = mockLeads.filter(l => !l.deleted_at);
    switch (groupBy) {
      case 'Vertical': {
        return mockVerticals.map(v => {
          const vLeads = leads.filter(l => l.vertical_id === v.id);
          const payouts = mockPayoutLines.filter(p => {
            const lead = leads.find(l => l.id === p.lead_id);
            return lead?.vertical_id === v.id;
          });
          return {
            dimension: v.name,
            'Leads received': vLeads.length,
            'Leads routed': vLeads.filter(l => l.routing_status === 'assigned').length,
            'Gross payout EUR': payouts.reduce((s, p) => s + p.amount_eur, 0),
            'Avg quality score': vLeads.length > 0
              ? vLeads.reduce((s, l) => s + (l.quality_score || 0), 0) / vLeads.length
              : 0,
            'Disputes raised': 0,
          };
        });
      }
      case 'Geo': {
        return mockGeos.map(g => {
          const gLeads = leads.filter(l => l.geo_id === g.id);
          return {
            dimension: g.name,
            'Leads received': gLeads.length,
            'Leads routed': gLeads.filter(l => l.routing_status === 'assigned').length,
            'Gross payout EUR': 0,
            'Avg quality score': gLeads.length > 0
              ? gLeads.reduce((s, l) => s + (l.quality_score || 0), 0) / gLeads.length
              : 0,
            'Disputes raised': 0,
          };
        });
      }
      case 'Partner': {
        return mockPartners.map(p => {
          const pLeads = leads.filter(l => l.assigned_partner_id === p.id);
          const payouts = mockPayoutLines.filter(pl => pl.partner_id === p.id);
          return {
            dimension: p.display_name,
            'Leads received': pLeads.length,
            'Leads routed': pLeads.filter(l => l.routing_status === 'assigned').length,
            'Gross payout EUR': payouts.reduce((s, pl) => s + pl.amount_eur, 0),
            'Avg quality score': pLeads.length > 0
              ? pLeads.reduce((s, l) => s + (l.quality_score || 0), 0) / pLeads.length
              : 0,
            'Disputes raised': 0,
          };
        });
      }
      case 'Status': {
        const statuses = Array.from(new Set(leads.map(l => l.status)));
        return statuses.map(s => {
          const sLeads = leads.filter(l => l.status === s);
          return {
            dimension: s,
            'Leads received': sLeads.length,
            'Leads routed': sLeads.filter(l => l.routing_status === 'assigned').length,
            'Gross payout EUR': 0,
            'Avg quality score': sLeads.length > 0
              ? sLeads.reduce((sum, l) => sum + (l.quality_score || 0), 0) / sLeads.length
              : 0,
            'Disputes raised': 0,
          };
        });
      }
      default: return [];
    }
  }, [groupBy]);

  const exportCSV = () => {
    const headers = ['Dimension', ...selectedMetrics];
    const csv = [
      headers.join(','),
      ...data.map(d => [d.dimension, ...selectedMetrics.map(m => d[m as keyof typeof d])].join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report-${groupBy.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Reports</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>Multi-dimensional analysis</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCSV} className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-all"
            style={{ color: 'var(--text-tertiary)', borderColor: 'var(--border-primary)', background: 'var(--bg-input)' }}
          >
            <Download size={14} />
            Export
          </button>
          <button
            onClick={() => {
              const config = { groupBy, selectedMetrics, viewMode };
              localStorage.setItem('saved-report', JSON.stringify(config));
              // feedback via temporary state would need more hooks — keep simple
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-all"
            style={{ color: 'var(--text-tertiary)', borderColor: 'var(--border-primary)', background: 'var(--bg-input)' }}
          >
            <Save size={14} />
            Save
          </button>
          <button
            onClick={() => {
              const token = Math.random().toString(36).slice(2);
              const url = `${window.location.origin}/reports/shared/${token}`;
              navigator.clipboard.writeText(url);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-all"
            style={{ color: 'var(--text-tertiary)', borderColor: 'var(--border-primary)', background: 'var(--bg-input)' }}
          >
            <Share2 size={14} />
            Share
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="rounded-xl p-5 space-y-4 border"
        style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'var(--text-muted)' }}>Group By</label>
            <BaseSelect
              value={groupBy}
              onChange={val => setGroupBy(val)}
              options={dimensions.map(d => ({ value: d, label: d }))}
            />
          </div>
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'var(--text-muted)' }}>Metrics</label>
            <div className="flex flex-wrap gap-1.5">
              {metrics.map(m => (
                <button
                  key={m}
                  onClick={() => {
                    setSelectedMetrics(prev =>
                      prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]
                    );
                  }}
                  className="px-2.5 py-1 text-[10px] rounded-full border transition-all duration-200"
                  style={{
                    background: selectedMetrics.includes(m) ? 'var(--accent)' : 'var(--bg-input)',
                    color: selectedMetrics.includes(m) ? '#fff' : 'var(--text-tertiary)',
                    borderColor: selectedMetrics.includes(m) ? 'var(--accent)' : 'var(--border-primary)',
                  }}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'var(--text-muted)' }}>View</label>
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('list')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-all duration-200"
                style={{
                  background: viewMode === 'list' ? 'var(--accent)' : 'var(--bg-input)',
                  color: viewMode === 'list' ? '#fff' : 'var(--text-tertiary)',
                  borderColor: viewMode === 'list' ? 'var(--accent)' : 'var(--border-primary)',
                }}
              >
                <LayoutGrid size={12} />
                List
              </button>
              <button
                onClick={() => setViewMode('chart')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-all duration-200"
                style={{
                  background: viewMode === 'chart' ? 'var(--accent)' : 'var(--bg-input)',
                  color: viewMode === 'chart' ? '#fff' : 'var(--text-tertiary)',
                  borderColor: viewMode === 'chart' ? 'var(--accent)' : 'var(--border-primary)',
                }}
              >
                <BarChart3 size={12} />
                Chart
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Results — Card Feed, No Table */}
      {viewMode === 'list' ? (
        <div className="rounded-xl border overflow-hidden"
          style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}
        >
          {/* Column headers */}
          <div className="px-5 py-3 flex items-center text-[10px] font-medium uppercase tracking-wider border-b"
            style={{ borderColor: 'var(--border-separator)', color: 'var(--text-muted)', background: 'var(--bg-input)' }}
          >
            <span className="flex-1">{groupBy}</span>
            {selectedMetrics.map(m => (
              <span key={m} className="w-24 text-right">{m}</span>
            ))}
          </div>
          {data.map((row, i) => (
            <div key={i} className="px-5 py-4 flex items-center transition-colors hover-surface"
              style={{ borderBottom: '1px solid var(--border-separator)' }}
            >
              <span className="flex-1 text-[13px] font-medium truncate" style={{ color: 'var(--text-secondary)' }}>{row.dimension}</span>
              {selectedMetrics.map(m => {
                const val = row[m as keyof typeof row] as number;
                return (
                  <span key={m} className="w-24 text-right text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                    {m.includes('score') ? val.toFixed(2) : m.includes('EUR') ? `€${val.toLocaleString()}` : val.toLocaleString()}
                  </span>
                );
              })}
            </div>
          ))}
          {data.length === 0 && (
            <div className="text-center py-8">
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No data available</p>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-xl p-5 border"
          style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}
        >
          <SimpleBarChart data={data} selectedMetrics={selectedMetrics} />
        </div>
      )}
    </div>
  );
}

function SimpleBarChart({ data, selectedMetrics }: { data: any[]; selectedMetrics: string[] }) {
  const maxVal = Math.max(...data.map(d => Math.max(...selectedMetrics.map(m => (d[m] as number) || 0))));
  const colors = ['var(--text-secondary)', 'var(--accent)', 'var(--success-dot)', 'var(--warning-dot)', 'var(--danger-dot)'];
  return (
    <div className="space-y-8">
      {selectedMetrics.map((metric, mi) => (
        <div key={metric}>
          <p className="text-[11px] font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>{metric}</p>
          <div className="space-y-2.5">
            {data.map((row, i) => {
              const val = (row[metric] as number) || 0;
              const pct = maxVal > 0 ? (val / maxVal) * 100 : 0;
              return (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs w-28 truncate" style={{ color: 'var(--text-secondary)' }}>{row.dimension}</span>
                  <div className="flex-1 h-5 rounded-lg overflow-hidden" style={{ background: 'var(--bg-input)' }}>
                    <div
                      className="h-full rounded-lg transition-all duration-700"
                      style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: colors[mi % 5] }}
                    />
                  </div>
                  <span className="text-xs w-20 text-right font-medium" style={{ color: 'var(--text-secondary)' }}>
                    {metric.includes('EUR') ? `€${val.toLocaleString()}` : val.toLocaleString()}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}